/**
 * Browser-side image compression for uploads (signed handover sheets).
 *
 * A phone photo of an A4 sheet is 2–8 MB; downscaled to 1600 px on the
 * long side and re-encoded as JPEG at 75% it is 150–400 KB and still
 * perfectly legible — 10–20× smaller, which is what keeps the 1 GB
 * storage budget comfortable. PDFs and anything the browser can't
 * decode (e.g. HEIC on Chrome) pass through unchanged. If compression
 * doesn't actually shrink the file, the original is kept.
 */

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** "320 KB", "3.2 MB" — for the UI and toasts. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  const mb = n / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

/** New file name for a re-encoded image: same stem, .jpg extension. */
export function jpegName(name: string): string {
  const stem = name.replace(/\.[^.]+$/, "") || "scan";
  return `${stem}.jpg`;
}

export async function compressImage(
  file: File,
  maxEdge = 1600,
  quality = 0.75,
): Promise<File> {
  if (!file.type.startsWith("image/") || typeof createImageBitmap !== "function") return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file; // format the browser can't decode — upload as-is
  }
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.fillStyle = "#ffffff"; // flatten transparency (PNG) onto white paper
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], jpegName(file.name), { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}
